import { useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, X } from "lucide-react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useCreateToken } from "@/hooks/useCreateToken";
import { OneTimeTokenDisplay } from "./OneTimeTokenDisplay";
import type { CreateTokenResponse, TokenType } from "@/types/token";

interface CreateTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTokenType?: TokenType;
}

const urlPattern = /^https?:\/\/.+/;

const formSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(255, "Name too long"),
    description: z.string().max(1000, "Description too long").optional(),
    token_type: z.enum(["backend", "frontend", "cli"]),
    allowed_origins: z.string().optional(),
    expiration: z.enum(["never", "30", "90", "365"]),
  })
  .superRefine((data, ctx) => {
    if (data.token_type === "frontend") {
      const origins = data.allowed_origins
        ?.split(",")
        .map((o) => o.trim())
        .filter(Boolean);
      if (!origins?.length) {
        ctx.addIssue({
          code: "custom",
          message: "At least one origin is required for frontend tokens",
          path: ["allowed_origins"],
        });
        return;
      }
      for (const origin of origins) {
        if (!urlPattern.test(origin)) {
          ctx.addIssue({
            code: "custom",
            message: `Invalid URL format: ${origin}`,
            path: ["allowed_origins"],
          });
          return;
        }
      }
    }
  });

type FormData = z.infer<typeof formSchema>;

function getExpirationDate(expiration: string): string | undefined {
  if (expiration === "never") return undefined;
  const days = parseInt(expiration, 10);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function CreateTokenDialog({
  open,
  onOpenChange,
  defaultTokenType = "backend",
}: CreateTokenDialogProps) {
  const [createdToken, setCreatedToken] = useState<CreateTokenResponse | null>(
    null,
  );
  const createToken = useCreateToken();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      token_type: defaultTokenType,
      allowed_origins: "",
      expiration: "never",
    },
  });

  const tokenType = useWatch({ control: form.control, name: "token_type" });

  // Auto-select 90-day expiry when CLI is chosen, reset to "never" otherwise
  useEffect(() => {
    if (tokenType === "cli") {
      form.setValue("expiration", "90");
    } else {
      form.setValue("expiration", "never");
    }
  }, [tokenType, form]);

  // Reset form when dialog opens or defaultTokenType changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: "",
        description: "",
        token_type: defaultTokenType,
        allowed_origins: "",
        expiration: "never",
      });
    }
  }, [open, defaultTokenType, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const origins = data.allowed_origins
        ?.split(",")
        .map((o) => o.trim())
        .filter(Boolean);

      const result = await createToken.mutateAsync({
        name: data.name,
        description: data.description || undefined,
        token_type: data.token_type,
        allowed_origins:
          data.token_type === "frontend" && origins?.length
            ? origins
            : undefined,
        expires_at: getExpirationDate(data.expiration),
      });

      setCreatedToken(result);
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleClose = () => {
    form.reset();
    setCreatedToken(null);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={createdToken ? handleClose : onOpenChange}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {createdToken ? "Token Created" : "Generate Access Token"}
          </DialogTitle>
          {!createdToken && (
            <DialogDescription>
              Create a new token to authenticate OTEL collectors, browser SDKs,
              or CLI tools.
            </DialogDescription>
          )}
        </DialogHeader>

        {createdToken ? (
          <OneTimeTokenDisplay
            token={createdToken.token}
            tokenType={createdToken.token_type}
            onClose={handleClose}
          />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Production API Key" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Used for backend services"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="token_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Token Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select token type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="backend">Backend</SelectItem>
                        <SelectItem value="frontend">Frontend</SelectItem>
                        <SelectItem value="cli">CLI</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {tokenType === "backend"
                        ? "For server-side OTEL collectors"
                        : tokenType === "cli"
                          ? "For CLI read-only access (90-day default expiry)"
                          : "For browser-based SDKs (requires allowed origins)"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {tokenType === "frontend" && (
                <FormField
                  control={form.control}
                  name="allowed_origins"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Allowed Origins</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com, https://app.example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Comma-separated list of allowed origins
                      </FormDescription>
                      <FormMessage />
                      {field.value && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {field.value
                            .split(",")
                            .map((o) => o.trim())
                            .filter(Boolean)
                            .map((origin) => (
                              <Badge
                                key={origin}
                                variant="outline"
                                className="font-mono text-xs"
                              >
                                {origin}
                                <button
                                  type="button"
                                  className="ml-1 hover:text-destructive"
                                  onClick={() => {
                                    const origins = field.value
                                      ?.split(",")
                                      .map((o) => o.trim())
                                      .filter((o) => o !== origin)
                                      .join(", ");
                                    field.onChange(origins);
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                        </div>
                      )}
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="expiration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel id="expiration-label">Expiration</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      aria-labelledby="expiration-label"
                    >
                      <FormControl>
                        <SelectTrigger aria-label="Expiration">
                          <SelectValue placeholder="Select expiration" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="never">Never expires</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="365">1 year</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={createToken.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createToken.isPending}>
                  {createToken.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {createToken.isPending ? "Generating..." : "Generate Token"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
